import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { ADSENSE_GLOBAL_STATUS_ATTRIBUTE, type AdsenseGlobalScriptStatus, parseAdsenseGlobalScript } from "@/lib/adsense";
import { hasHeadScript, loadHeadScriptOnce, removeHeadScript } from "@/lib/scriptLoader";

const ADSENSE_GLOBAL_SCRIPT_ID = "ad-provider-adsense";

function setValidationStatus(status: AdsenseGlobalScriptStatus) {
  document.documentElement.setAttribute(ADSENSE_GLOBAL_STATUS_ATTRIBUTE, status);
}

export default function GlobalAdsenseScriptManager() {
  const { settings } = useSiteSettings();
  const location = useLocation();
  const adsenseSettings = settings.adSettings.providers.adsense;

  useEffect(() => {
    const scriptCode = adsenseSettings.globalScriptCode.trim();

    if (!adsenseSettings.enabled || !scriptCode) {
      removeHeadScript(ADSENSE_GLOBAL_SCRIPT_ID);
      setValidationStatus("removed");
      return;
    }

    const parsedScript = parseAdsenseGlobalScript(scriptCode);
    if (!parsedScript) {
      removeHeadScript(ADSENSE_GLOBAL_SCRIPT_ID);
      setValidationStatus("invalid");
      return;
    }

    void loadHeadScriptOnce(ADSENSE_GLOBAL_SCRIPT_ID, parsedScript.src, {
      crossorigin: parsedScript.crossorigin,
    })
      .then(() => {
        setValidationStatus(hasHeadScript(ADSENSE_GLOBAL_SCRIPT_ID, parsedScript.src) ? "active" : "invalid");
      })
      .catch(() => {
        removeHeadScript(ADSENSE_GLOBAL_SCRIPT_ID);
        setValidationStatus("invalid");
      });
  }, [adsenseSettings.enabled, adsenseSettings.globalScriptCode, location.pathname]);

  return null;
}
